import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Configurator, ConfiguratorConfig } from '../types';
import templateLoaderInstance from '../templates/templateLoader';
import configuratorsData from '../configurators.json';

interface ConfiguratorContextType {
  availableConfigurators: Configurator[];
  selectedConfiguratorId: string | null;
  currentConfig: ConfiguratorConfig | null;
  selectedLanguage: string;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  setSelectedConfiguratorId: (id: string) => void;
  setSelectedLanguage: (language: string) => void;
  sketchfabApi: any;
  setSketchfabApi: (api: any) => void;
  updateUrlWithConfiguratorId: (configId: string) => void;
}

const ConfiguratorContext = createContext<ConfiguratorContextType | undefined>(undefined);

interface ConfiguratorProviderProps {
  children: ReactNode;
}

export const ConfiguratorProvider: React.FC<ConfiguratorProviderProps> = ({ children }) => {
  const [availableConfigurators, setAvailableConfigurators] = useState<Configurator[]>([]);
  const [selectedConfiguratorId, setSelectedConfiguratorId] = useState<string | null>(null);
  const [currentConfig, setCurrentConfig] = useState<ConfiguratorConfig | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('en');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [sketchfabApi, setSketchfabApi] = useState<any>(null);

  // Filter configurators based on the selected language
  const filterConfiguratorsByLanguage = (configurators: Configurator[], language: string) => {
    return configurators.filter(configurator => {
      // If languages property doesn't exist or includes 'all', show for all languages
      if (!configurator.languages || configurator.languages.includes('all')) {
        return true;
      }
      // Otherwise, only show if the current language is in the languages array
      return configurator.languages.includes(language);
    });
  };

  // Parse URL parameters to get configurator ID
  const getConfiguratorIdFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('configurator');
  };

  // Update URL with current configurator ID
  const updateUrlWithConfiguratorId = (configId: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set('configurator', configId);
    window.history.pushState({}, '', url);
  };

  // Load available configurators on initial render
  useEffect(() => {
    try {
      const configurators = configuratorsData.configurators || [];
      const filteredConfigurators = filterConfiguratorsByLanguage(configurators, selectedLanguage);
      setAvailableConfigurators(filteredConfigurators);
      
      // Check URL parameters for configurator ID
      const urlConfigId = getConfiguratorIdFromUrl();
      
      if (urlConfigId) {
        // Verify that the configurator from URL exists in available configurators
        const configExists = filteredConfigurators.some(config => config.id === urlConfigId);
        
        if (configExists) {
          setSelectedConfiguratorId(urlConfigId);
        } else if (filteredConfigurators.length > 0) {
          // If configurator from URL doesn't exist, set the first one as default
          setSelectedConfiguratorId(filteredConfigurators[0].id);
        }
      } else if (filteredConfigurators.length > 0 && !selectedConfiguratorId) {
        // If no URL parameter and no selection, set the first configurator as default
        setSelectedConfiguratorId(filteredConfigurators[0].id);
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading configurators:', error);
      setIsLoading(false);
    }
  }, [selectedLanguage]); // Re-run when language changes

  // Load configuration when selected configurator changes or language changes
  useEffect(() => {
    const loadConfiguration = async () => {
      if (!selectedConfiguratorId || availableConfigurators.length === 0) {
        return;
      }

      setIsLoading(true);

      try {
        const selectedConfigurator = availableConfigurators.find(
          config => config.id === selectedConfiguratorId
        );

        if (selectedConfigurator) {
          // Load template based on configurator type using our templateLoader
          const config = templateLoaderInstance.loadTemplate(selectedConfigurator);
          setCurrentConfig(config);
        }
      } catch (error) {
        console.error('Error loading configuration:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadConfiguration();
  }, [selectedConfiguratorId, availableConfigurators, selectedLanguage]);

  // Update URL when selected configurator changes
  useEffect(() => {
    if (selectedConfiguratorId) {
      updateUrlWithConfiguratorId(selectedConfiguratorId);
    }
  }, [selectedConfiguratorId]);

  const value = {
    availableConfigurators,
    selectedConfiguratorId,
    currentConfig,
    selectedLanguage,
    isLoading,
    setIsLoading,
    setSelectedConfiguratorId,
    setSelectedLanguage,
    sketchfabApi,
    setSketchfabApi,
    updateUrlWithConfiguratorId,
  };

  return (
    <ConfiguratorContext.Provider value={value}>
      {children}
    </ConfiguratorContext.Provider>
  );
};

export const useConfigurator = (): ConfiguratorContextType => {
  const context = useContext(ConfiguratorContext);
  if (context === undefined) {
    throw new Error('useConfigurator must be used within a ConfiguratorProvider');
  }
  return context;
};
